<template>
  <div class="bg-white border rounded-4 shadow-sm p-3 h-100">
    
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6 class="fw-bold mb-0">Job Reference</h6>
      <input
        type="date"
        v-model="selectedDate"
        class="form-control form-control-sm"
        style="width: 150px;"
        @change="fetchBookings"
      />
    </div>

    <!-- LOADING -->
    <div v-if="loading" class="text-center py-3 text-muted">
      Loading...
    </div>

    <!-- EMPTY -->
    <div v-else-if="bookings.length === 0" class="text-center text-muted">
      No jobs found
    </div>

    <!-- LIST -->
    <div v-else class="job-list">
      <div
        v-for="b in bookings"
        :key="b.id"
        class="border rounded mb-2"
      >
        <!-- HEADER (CLICK TO TOGGLE) -->
        <div
          class="p-2 bg-light d-flex justify-content-between align-items-center cursor-pointer"
          @click="toggle(b.id)"
        >
          <div>
            <div class="fw-semibold">
              {{ b.id }} — {{ b.vesselName || 'No Vessel' }}
            </div>
            <div class="small text-muted">
              {{ formatDT(b.startTime) }}
            </div>
          </div>

          <div>
            <span :class="'badge ' + getStatusColor(b.status)">
              {{ b.status || '-' }}
            </span>
          </div>
        </div>

        <!-- BODY -->
        <div v-if="expanded[b.id]" class="p-2 border-top small">
          
          <div><b>Agent:</b> {{ getClientName(b.clientId) }}</div>
          <div><b>Counter:</b> {{ b.counterNumber || '-' }}</div>
          <div><b>Type:</b> {{ b.bookingType || '-' }}</div>
          <div><b>Route:</b> {{ b.startLocation }} → {{ b.endLocation }}</div>
          <div><b>Crew:</b> {{ getCrewDisplayName(b) }}</div>
          <div><b>Flight:</b> {{ displayFlightNumbers(b.flightNumbers) }}</div>

          <div v-if="b.vouchers && b.vouchers.length > 0" class="mt-1">
            <b>Vouchers:</b>
            <div class="d-flex flex-wrap gap-1 mt-1">
              <button
                v-for="v in b.vouchers"
                :key="v"
                type="button"
                class="badge bg-light text-dark border voucher-badge-btn"
                @click.stop="openVoucherModal(v)"
              >
                {{ v }}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- ================= VOUCHER MODAL ================= -->
    <div
      v-if="showVoucherModal"
      class="voucher-modal-backdrop"
      @click.self="closeVoucherModal"
    >
      <div class="voucher-modal card shadow-lg border-0">
        <div class="card-header d-flex justify-content-between align-items-center">
          <div>
            <h6 class="mb-0 fw-bold">Voucher Preview</h6>
            <small class="text-muted">{{ selectedVoucherId }}</small>
          </div>
          <button
            class="btn btn-sm btn-outline-secondary"
            @click="closeVoucherModal"
          >
            ✕
          </button>
        </div>

        <div class="card-body p-0">
          <div v-if="voucherLoading" class="text-center py-5 text-muted">
            Loading voucher preview...
          </div>

          <div v-else-if="voucherError" class="text-center py-5 text-danger">
            {{ voucherError }}
          </div>

          <div
            v-else-if="voucherPreviewUrl"
            class="text-center p-3"
            >
            <img
                :src="voucherPreviewUrl"
                alt="Voucher Preview"
                class="img-fluid rounded border"
                style="max-height: 75vh;"
            />
            </div>

          <div v-else class="text-center py-5 text-muted">
            No preview available
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { getAuth } from 'firebase/auth'

export default {
  props: ['clients'],
  emits: ['select-job'],

  data() {
    return {
      selectedDate: '',
      bookings: [],
      expanded: {},
      loading: false,

      // modal
      showVoucherModal: false,
      selectedVoucherId: '',
      voucherPreviewUrl: '',
      voucherLoading: false,
      voucherError: ''
    }
  },

  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser
      const token = await user.getIdToken()
      return { Authorization: token }
    },

    async fetchBookings() {
      this.loading = true
      try {
        const headers = await this.getAuthHeader()

        const res = await axios.get(
          `${this.$apiURL}/api/bookings`,
          {
            headers,
            params: { date: this.selectedDate }
          }
        )

        this.bookings = (res.data && res.data.bookings) ? res.data.bookings : []
      } catch (err) {
        console.error(err)
        this.bookings = []
      } finally {
        this.loading = false
      }
    },

    toggle(id) {
      this.expanded[id] = !this.expanded[id]
    },

    formatDT(val) {
      if (!val) return '-'
      return new Date(val).toLocaleString('en-SG', { hour12: false })
    },

    getClientName(id) {
      if (!id) return '-'
      const c = this.clients.find(function(client) {
        return client.id === id
      })
      return c ? (c.name || c.contactPerson) : '-'
    },

    displayFlightNumbers(fns) {
      try {
        if (typeof fns === 'string') fns = JSON.parse(fns)
        if (!Array.isArray(fns)) return ''
        return fns.map(function(f) {
          return (f && (f.number || f.flight)) || ''
        }).filter(Boolean).join(', ')
      } catch (e) {
        return ''
      }
    },

    getCrewDisplayName(b) {
      if (b.crewName) return b.crewName
      if (Array.isArray(b.crewMembers)) {
        return b.crewMembers.map(function(c) {
          return c.name
        }).join(', ')
      }
      return '-'
    },

    // ================= VOUCHER MODAL =================
    async openVoucherModal(voucherId) {
        this.showVoucherModal = true
        this.selectedVoucherId = voucherId
        this.voucherLoading = true
        this.voucherError = ''
        this.voucherPreviewUrl = ''

        try {
            const headers = await this.getAuthHeader()

            const res = await axios.get(
            `${this.$apiURL}/api/vouchers/${voucherId}/preview-url`,
            { headers }
            )

            this.voucherPreviewUrl = res.data.previewUrl || ''

            if (!this.voucherPreviewUrl) {
            this.voucherError = 'Preview URL not found'
            }

        } catch (err) {
            console.error(err)
            this.voucherError = 'Failed to load voucher preview'
        } finally {
            this.voucherLoading = false
        }
    },

    closeVoucherModal() {
        this.showVoucherModal = false
        this.selectedVoucherId = ''
        this.voucherLoading = false
        this.voucherError = ''
        this.voucherPreviewUrl = ''
    },
    getStatusColor(status) {
      switch ((status || '').toLowerCase()) {
        case 'new':
          return 'bg-blue-100 text-blue-800'
        case 'assigned':
          return 'bg-indigo-100 text-indigo-800'
        case 'assigning':
          return 'bg-purple-100 text-purple-800'
        case 'on route':
          return 'bg-yellow-100 text-yellow-800'
        case 'arrived':
          return 'bg-amber-100 text-amber-800'
        case 'on board':
          return 'bg-teal-100 text-teal-800'
        case 'dop':
        case 'completed':
          return 'bg-green-100 text-green-800'
        case 'cancelled':
          return 'bg-red-100 text-red-800'
        default:
          return 'bg-gray-100 text-gray-800'
        
      }
    },
  },

  mounted() {
    const today = new Date().toISOString().slice(0, 10)
    this.selectedDate = today
    this.fetchBookings()
  },

}
</script>

<style scoped>
.job-list {
  max-height: 700px;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}

.voucher-badge-btn {
  border: 1px solid #dee2e6;
  background: #f8f9fa;
  padding: 6px 10px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
}

.voucher-badge-btn:hover {
  background: #e9ecef;
}

.voucher-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.voucher-modal {
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  overflow: hidden;
  border-radius: 16px;
}

.card-body {
  background: #f8f9fa;
}
</style>